import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = '/ciaolead-logo-full.png';

/*
 * P6.7 — Public Privacy Policy page (reachable without login, route: /privacy).
 *
 * No placeholders remain. Two things to keep in sync whenever the policy text
 * changes:
 *   - the "Last updated" date in the header below
 *   - the studio contact email, which appears in THREE places: the data-controller
 *     block, the rights-exercise paragraph, and the closing contact block
 */
const Privacy = () => {
  return (
    <div className="min-h-screen bg-[var(--bg-page)] world-stars" data-testid="privacy-page">
      <div className="relative z-10 max-w-3xl mx-auto px-5 py-10 sm:px-8 sm:py-14">
        {/* Lightweight standalone header — no app sidebar/chrome */}
        <div className="flex items-center justify-between mb-8">
          <img src={LOGO_URL} alt="CiaoLead" className="h-10 w-auto object-contain" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
        </div>

        <article className="world-card p-6 sm:p-10 text-slate-300 leading-relaxed space-y-6">
          <header className="space-y-2">
            <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
            <p className="text-sm text-slate-400">
              <span className="font-semibold text-slate-200">Last updated:</span> 14 August 2026
            </p>
          </header>

          <p>
            This Privacy Policy explains how CiaoLead (&quot;we&quot;, &quot;us&quot;, &quot;the
            Service&quot;) collects, uses, and protects your personal data when you use the Service.
            We are committed to handling your data lawfully, transparently, and in accordance with the
            EU General Data Protection Regulation (GDPR).
          </p>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">1. Who we are (Data Controller)</h2>
            <p>The data controller responsible for your personal data is:</p>
            <p>
              <span className="font-semibold text-slate-200">EMS (Echo Maik Studio)</span>
              <br />
              Contact email: <span className="font-semibold text-slate-200">hello@echomaikstudio.com</span>
            </p>
            <p>
              For any question about this policy or about how your data is handled, you can contact us
              at the email address above.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">2. What data we collect</h2>
            <p>
              When you create an account and use CiaoLead, we collect and process the following
              personal data:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <span className="font-semibold text-slate-200">Account data:</span> your email address,
                your password (stored only in an encrypted/hashed form — we never store or have access
                to your plain-text password), and the company name you provide at sign-up.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Account status data:</span> the status of
                your account (for example, pending approval, active) and the date your account was
                created.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Operational data:</span> the searches you
                run within the Service and their results, which are stored so you can review, manage, and
                export them.
              </li>
            </ul>
            <p>
              We do <span className="font-semibold text-slate-200">not</span> collect special categories
              of personal data (such as health, political opinions, or biometric data), and we do{' '}
              <span className="font-semibold text-slate-200">not</span> use tracking cookies or
              third-party advertising.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white">
              3. Why we process your data (Purpose and Legal Basis)
            </h2>
            <p>We process your personal data only for the following purposes:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
                <thead className="bg-white/5 text-slate-200">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2 border-b border-white/10">
                      Purpose
                    </th>
                    <th className="text-left font-semibold px-3 py-2 border-b border-white/10">
                      Legal basis (GDPR)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 border-b border-white/5">
                      Creating and managing your account, allowing you to log in
                    </td>
                    <td className="px-3 py-2 border-b border-white/5">
                      Performance of a contract (Art. 6(1)(b))
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 border-b border-white/5">
                      Reviewing and approving new sign-ups to protect the Service
                    </td>
                    <td className="px-3 py-2 border-b border-white/5">
                      Legitimate interest (Art. 6(1)(f))
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">
                      Storing your searches and results so you can use the Service
                    </td>
                    <td className="px-3 py-2">Performance of a contract (Art. 6(1)(b))</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              We do not sell your personal data, and we do not share it with third parties for marketing
              purposes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">4. How long we keep your data</h2>
            <p>
              We keep your personal data for as long as your account is active. If your account is
              rejected, deleted, or you ask us to remove it, we delete your personal data from our
              systems, except where we are required to retain certain information to comply with a legal
              obligation.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">5. Where your data is stored</h2>
            <p>
              Your data is stored on secure cloud infrastructure (database and hosting services) used to
              operate the Service. Access to the data is restricted and protected by authentication.
              Passwords are stored only in hashed form.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">6. Your rights</h2>
            <p>Under the GDPR, you have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <span className="font-semibold text-slate-200">Access</span> — you can ask for a copy of
                the personal data we hold about you.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Rectification</span> — you can ask us to
                correct inaccurate or incomplete data.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Erasure</span> — you can ask us to delete
                your personal data (&quot;right to be forgotten&quot;).
              </li>
              <li>
                <span className="font-semibold text-slate-200">Restriction</span> — you can ask us to
                limit how we use your data.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Objection</span> — you can object to
                processing based on our legitimate interest.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Portability</span> — you can ask to
                receive your data in a structured, commonly used format.
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <span className="font-semibold text-slate-200">hello@echomaikstudio.com</span>. We will respond
              within the timeframes required by law. You also have the right to lodge a complaint with
              your local data protection authority.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">7. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make significant changes, we
              will update the &quot;Last updated&quot; date at the top of this page. We encourage you to
              review this policy periodically.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-white">8. Contact</h2>
            <p>
              For any questions about this Privacy Policy or your personal data, please contact:
            </p>
            <p>
              <span className="font-semibold text-slate-200">EMS (Echo Maik Studio)</span>
              <br />
              Email: <span className="font-semibold text-slate-200">hello@echomaikstudio.com</span>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default Privacy;
